import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { docTemplatesAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import {
  Save, Upload, ArrowLeft, PenTool, Type, Mail, Phone, User, Hash,
  Calendar, CheckSquare, ChevronDown, Trash2, FileText, Tag, Building,
  Truck, DollarSign, Clock, MapPin
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FIELD_TYPES = [
  { type: 'signature', label: 'Signature', icon: PenTool, color: '#7C3AED', w: 200, h: 50 },
  { type: 'initials', label: 'Initials', icon: PenTool, color: '#7C3AED', w: 80, h: 40 },
  { type: 'text', label: 'Text Field', icon: Type, color: '#2563EB', w: 180, h: 28 },
  { type: 'name', label: 'Name', icon: User, color: '#059669', w: 180, h: 28 },
  { type: 'email', label: 'Email', icon: Mail, color: '#D97706', w: 200, h: 28 },
  { type: 'phone', label: 'Phone', icon: Phone, color: '#DC2626', w: 160, h: 28 },
  { type: 'date', label: 'Date Field', icon: Calendar, color: '#0891B2', w: 120, h: 28 },
  { type: 'checkbox', label: 'Checkbox', icon: CheckSquare, color: '#4F46E5', w: 24, h: 24 },
  { type: 'dropdown', label: 'Dropdown', icon: ChevronDown, color: '#9333EA', w: 160, h: 28 },
  { type: 'number', label: 'Number', icon: Hash, color: '#EA580C', w: 120, h: 28 },
];

const CATEGORY_ICONS = {
  Client: User,
  Equipment: Truck,
  Deal: DollarSign,
  Financial: DollarSign,
  Dates: Calendar,
  Dealership: Building,
};

const CATEGORY_COLORS = {
  Client: '#059669',
  Equipment: '#2563EB',
  Deal: '#D97706',
  Financial: '#DC2626',
  Dates: '#0891B2',
  Dealership: '#7C3AED',
};

export default function TemplateBuilder() {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedField, setSelectedField] = useState(null);
  const [draggingType, setDraggingType] = useState(null);
  const [draggingTag, setDraggingTag] = useState(null);
  const [movingField, setMovingField] = useState(null);
  const [moveOffset, setMoveOffset] = useState({ x: 0, y: 0 });
  const [resizingField, setResizingField] = useState(null);
  const [resizeHandle, setResizeHandle] = useState(null);
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, w: 0, h: 0 });
  const [editingField, setEditingField] = useState(null);
  const [editingLabel, setEditingLabel] = useState('');
  const [dataTags, setDataTags] = useState({});
  const [sidebarTab, setSidebarTab] = useState('data');
  const [expandedCategory, setExpandedCategory] = useState('Client');
  const pageRefs = useRef({});
  const labelInputRef = useRef(null);

  const load = useCallback(async () => {
    try { const res = await docTemplatesAPI.get(templateId); setTemplate(res.data); }
    catch { toast.error('Failed to load'); }
    setLoading(false);
  }, [templateId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    docTemplatesAPI.getDataTags().then(res => setDataTags(res.data || {})).catch(() => {});
  }, []);

  useEffect(() => {
    if (editingField && labelInputRef.current) {
      labelInputRef.current.focus();
      labelInputRef.current.select();
    }
  }, [editingField]);

  const handleUploadPdf = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await docTemplatesAPI.uploadPdf(templateId, file);
      setTemplate(res.data);
      toast.success(`PDF uploaded - ${res.data.page_count} pages`);
    } catch { toast.error('Failed to upload PDF'); }
    setUploading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await docTemplatesAPI.update(templateId, {
        name: template.name,
        description: template.description,
        fields: template.fields,
      });
      toast.success('Template saved');
    } catch { toast.error('Save failed'); }
    setSaving(false);
  };

  const handleDeletePdf = async () => {
    if (!window.confirm('Delete this PDF? All placed fields will also be removed.')) return;
    setUploading(true);
    try {
      const res = await docTemplatesAPI.deletePdf(templateId);
      setTemplate(res.data);
      setSelectedField(null);
      toast.success('PDF deleted');
    } catch { toast.error('Failed to delete PDF'); }
    setUploading(false);
  };

  // Drag from sidebar - form field
  const handleDragStart = (type) => { setDraggingType(type); setDraggingTag(null); };

  // Drag from sidebar - data tag
  const handleTagDragStart = (tag, category) => { setDraggingTag({ ...tag, category }); setDraggingType(null); };

  const handlePageDrop = (e, pageNum) => {
    e.preventDefault();
    const rect = pageRefs.current[pageNum]?.getBoundingClientRect();
    if (!rect) return;
    const pageImg = template.page_images.find(p => p.page === pageNum);
    if (!pageImg) return;
    const scaleX = pageImg.width / rect.width;
    const scaleY = pageImg.height / rect.height;

    if (draggingTag) {
      // Data tag drop
      const color = CATEGORY_COLORS[draggingTag.category] || '#059669';
      const newField = {
        id: `field_${Date.now()}`,
        type: 'data_tag',
        tag_key: draggingTag.key,
        label: draggingTag.label,
        category: draggingTag.category,
        page: pageNum,
        x: (e.clientX - rect.left) * scaleX,
        y: (e.clientY - rect.top) * scaleY,
        width: 180 * scaleX,
        height: 22 * scaleY,
        font_size: 9,
        required: false,
        color,
      };
      setTemplate(t => ({ ...t, fields: [...(t.fields || []), newField] }));
      setSelectedField(newField.id);
      setDraggingTag(null);
      return;
    }

    if (draggingType) {
      const fieldDef = FIELD_TYPES.find(f => f.type === draggingType);
      const newField = {
        id: `field_${Date.now()}`,
        type: draggingType,
        label: fieldDef?.label || draggingType,
        page: pageNum,
        x: (e.clientX - rect.left) * scaleX,
        y: (e.clientY - rect.top) * scaleY,
        width: (fieldDef?.w || 150) * scaleX,
        height: (fieldDef?.h || 30) * scaleY,
        required: false,
        options: draggingType === 'dropdown' ? ['Option 1', 'Option 2'] : undefined,
      };
      setTemplate(t => ({ ...t, fields: [...(t.fields || []), newField] }));
      setSelectedField(newField.id);
      setDraggingType(null);
    }
  };

  // Move field on page
  const handleFieldMouseDown = (e, field) => {
    if (editingField === field.id) return;
    e.stopPropagation();
    e.preventDefault();
    setSelectedField(field.id);
    const rect = pageRefs.current[field.page]?.getBoundingClientRect();
    if (!rect) return;
    const pageImg = template.page_images.find(p => p.page === field.page);
    if (!pageImg) return;
    const scaleX = pageImg.width / rect.width;
    const scaleY = pageImg.height / rect.height;
    setMoveOffset({
      x: e.clientX - (field.x / scaleX + rect.left),
      y: e.clientY - (field.y / scaleY + rect.top),
    });
    setMovingField(field.id);
  };

  const handleResizeMouseDown = (e, field, handle) => {
    e.stopPropagation();
    e.preventDefault();
    setResizingField(field.id);
    setResizeHandle(handle);
    setResizeStart({ x: e.clientX, y: e.clientY, w: field.width, h: field.height, fieldX: field.x, fieldY: field.y });
  };

  const handleFieldDoubleClick = (e, field) => {
    e.stopPropagation();
    if (field.type === 'data_tag') return; // Don't allow editing data tag labels inline
    setEditingField(field.id);
    setEditingLabel(field.label);
  };

  const saveInlineEdit = () => {
    if (editingField && editingLabel.trim()) updateField(editingField, 'label', editingLabel.trim());
    setEditingField(null);
    setEditingLabel('');
  };

  const cancelInlineEdit = () => { setEditingField(null); setEditingLabel(''); };

  const handleEditKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); saveInlineEdit(); }
    else if (e.key === 'Escape') cancelInlineEdit();
  };

  // Mouse move for moving fields
  useEffect(() => {
    if (!movingField) return;
    const handleMouseMove = (e) => {
      const field = template?.fields?.find(f => f.id === movingField);
      if (!field) return;
      const rect = pageRefs.current[field.page]?.getBoundingClientRect();
      if (!rect) return;
      const pageImg = template.page_images.find(p => p.page === field.page);
      if (!pageImg) return;
      const scaleX = pageImg.width / rect.width;
      const scaleY = pageImg.height / rect.height;
      const newX = (e.clientX - rect.left - moveOffset.x) * scaleX;
      const newY = (e.clientY - rect.top - moveOffset.y) * scaleY;
      setTemplate(t => ({
        ...t,
        fields: t.fields.map(f => f.id === movingField ? { ...f, x: Math.max(0, newX), y: Math.max(0, newY) } : f),
      }));
    };
    const handleMouseUp = () => setMovingField(null);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => { window.removeEventListener('mousemove', handleMouseMove); window.removeEventListener('mouseup', handleMouseUp); };
  }, [movingField, moveOffset, template]);

  // Mouse move for resizing fields
  useEffect(() => {
    if (!resizingField) return;
    const handleMouseMove = (e) => {
      const field = template?.fields?.find(f => f.id === resizingField);
      if (!field) return;
      const rect = pageRefs.current[field.page]?.getBoundingClientRect();
      if (!rect) return;
      const pageImg = template.page_images.find(p => p.page === field.page);
      if (!pageImg) return;
      const scaleX = pageImg.width / rect.width;
      const scaleY = pageImg.height / rect.height;
      const deltaX = (e.clientX - resizeStart.x) * scaleX;
      const deltaY = (e.clientY - resizeStart.y) * scaleY;
      let newWidth = resizeStart.w, newHeight = resizeStart.h, newX = resizeStart.fieldX, newY = resizeStart.fieldY;
      const minW = 30 * scaleX, minH = 20 * scaleY;
      if (resizeHandle.includes('e')) newWidth = Math.max(minW, resizeStart.w + deltaX);
      if (resizeHandle.includes('w')) { newWidth = Math.max(minW, resizeStart.w - deltaX); newX = resizeStart.fieldX + (resizeStart.w - newWidth); }
      if (resizeHandle.includes('s')) newHeight = Math.max(minH, resizeStart.h + deltaY);
      if (resizeHandle.includes('n')) { newHeight = Math.max(minH, resizeStart.h - deltaY); newY = resizeStart.fieldY + (resizeStart.h - newHeight); }
      setTemplate(t => ({ ...t, fields: t.fields.map(f => f.id === resizingField ? { ...f, width: newWidth, height: newHeight, x: Math.max(0, newX), y: Math.max(0, newY) } : f) }));
    };
    const handleMouseUp = () => { setResizingField(null); setResizeHandle(null); };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => { window.removeEventListener('mousemove', handleMouseMove); window.removeEventListener('mouseup', handleMouseUp); };
  }, [resizingField, resizeHandle, resizeStart, template]);

  const removeField = (id) => {
    setTemplate(t => ({ ...t, fields: t.fields.filter(f => f.id !== id) }));
    if (selectedField === id) setSelectedField(null);
    if (editingField === id) cancelInlineEdit();
  };

  const updateField = (id, key, val) => {
    setTemplate(t => ({ ...t, fields: t.fields.map(f => f.id === id ? { ...f, [key]: val } : f) }));
  };

  const selectedFieldData = template?.fields?.find(f => f.id === selectedField);

  if (loading) return <div className="animate-pulse h-96 bg-gray-100 rounded-lg" />;
  if (!template) return <div className="text-center py-20 text-gray-400">Template not found</div>;

  const pages = template.page_images || [];

  const ResizeHandles = ({ field, color }) => {
    const handleSize = 8;
    const handles = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
    const cursors = { nw: 'nw-resize', n: 'n-resize', ne: 'ne-resize', e: 'e-resize', se: 'se-resize', s: 's-resize', sw: 'sw-resize', w: 'w-resize' };
    const positions = {
      nw: { top: -handleSize/2, left: -handleSize/2 }, n: { top: -handleSize/2, left: '50%', transform: 'translateX(-50%)' },
      ne: { top: -handleSize/2, right: -handleSize/2 }, e: { top: '50%', right: -handleSize/2, transform: 'translateY(-50%)' },
      se: { bottom: -handleSize/2, right: -handleSize/2 }, s: { bottom: -handleSize/2, left: '50%', transform: 'translateX(-50%)' },
      sw: { bottom: -handleSize/2, left: -handleSize/2 }, w: { top: '50%', left: -handleSize/2, transform: 'translateY(-50%)' },
    };
    return handles.map(h => (
      <div key={h} className="absolute bg-white border-2 rounded-sm z-20"
        style={{ width: handleSize, height: handleSize, borderColor: color, cursor: cursors[h], ...positions[h] }}
        onMouseDown={(e) => handleResizeMouseDown(e, field, h)} data-testid={`resize-handle-${h}`} />
    ));
  };

  return (
    <div data-testid="template-builder-page" className="h-[calc(100vh-80px)] flex flex-col">
      {/* Top Bar */}
      <div className="flex items-center justify-between px-2 sm:px-4 py-2 border-b border-gray-200 bg-white flex-shrink-0">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/doc-templates')} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <Input value={template.name} onChange={e => setTemplate(t => ({ ...t, name: e.target.value }))} className="bg-transparent border-0 text-sm sm:text-base font-semibold w-32 sm:w-64 focus-visible:ring-0 focus-visible:bg-gray-50" data-testid="template-name" />
        </div>
        <div className="flex gap-1 sm:gap-2 flex-shrink-0">
          {pages.length > 0 && (
            <Button variant="outline" size="sm" className="border-red-300 text-red-600 hover:bg-red-50 text-xs hidden sm:flex" onClick={handleDeletePdf} disabled={uploading} data-testid="delete-pdf-btn">
              <Trash2 className="w-3 h-3 mr-1" /> Delete PDF
            </Button>
          )}
          <Button variant="outline" size="sm" className="border-gray-300 text-xs" onClick={() => document.getElementById('pdf-upload-input').click()} disabled={uploading} data-testid="upload-pdf-btn">
            <Upload className="w-3 h-3 sm:mr-1" /> <span className="hidden sm:inline">{uploading ? 'Processing...' : pages.length > 0 ? 'Replace PDF' : 'Upload PDF'}</span>
          </Button>
          <input id="pdf-upload-input" type="file" accept=".pdf" className="hidden" onChange={handleUploadPdf} />
          <Button size="sm" className="bg-[#E31937] hover:bg-[#C4122C] text-white text-xs" onClick={handleSave} disabled={saving} data-testid="save-template-btn">
            <Save className="w-3 h-3 sm:mr-1" /> <span className="hidden sm:inline">{saving ? 'Saving...' : 'Save Template'}</span>
          </Button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row flex-1 overflow-hidden">
        {/* Left Sidebar */}
        <div className="w-full sm:w-48 md:w-60 border-b sm:border-b-0 sm:border-r border-gray-200 bg-gray-50 flex-shrink-0 flex flex-col overflow-hidden max-h-[200px] sm:max-h-none" data-testid="field-sidebar">
          {/* Tabs */}
          <div className="flex border-b border-gray-200 flex-shrink-0">
            <button
              className={`flex-1 py-2 text-[11px] font-semibold transition-colors ${sidebarTab === 'data' ? 'text-[#E31937] border-b-2 border-[#E31937] bg-white' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => setSidebarTab('data')}
              data-testid="tab-data-tags"
            >
              <Tag className="w-3 h-3 inline mr-1" />Data Tags
            </button>
            <button
              className={`flex-1 py-2 text-[11px] font-semibold transition-colors ${sidebarTab === 'fields' ? 'text-[#E31937] border-b-2 border-[#E31937] bg-white' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => setSidebarTab('fields')}
              data-testid="tab-form-fields"
            >
              <Type className="w-3 h-3 inline mr-1" />Form Fields
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-3">
            {sidebarTab === 'data' ? (
              <>
                <p className="text-[10px] text-gray-400 uppercase font-bold mb-2">Drag data tags onto document</p>
                <div className="space-y-1">
                  {Object.entries(dataTags).map(([category, tags]) => {
                    const CatIcon = CATEGORY_ICONS[category] || Tag;
                    const catColor = CATEGORY_COLORS[category] || '#666';
                    const isExpanded = expandedCategory === category;
                    return (
                      <div key={category}>
                        <button
                          className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-left hover:bg-gray-100 transition-colors"
                          onClick={() => setExpandedCategory(isExpanded ? null : category)}
                          data-testid={`category-${category}`}
                        >
                          <CatIcon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: catColor }} />
                          <span className="text-xs font-semibold text-gray-700 flex-1">{category}</span>
                          <ChevronDown className={`w-3 h-3 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                        </button>
                        {isExpanded && (
                          <div className="ml-2 mt-0.5 space-y-0.5">
                            {tags.map(tag => (
                              <div
                                key={tag.key}
                                draggable
                                onDragStart={() => handleTagDragStart(tag, category)}
                                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded cursor-grab active:cursor-grabbing bg-white border border-gray-200 hover:border-gray-400 hover:shadow-sm transition-all"
                                data-testid={`data-tag-${tag.key}`}
                              >
                                <Tag className="w-3 h-3 flex-shrink-0" style={{ color: catColor }} />
                                <span className="text-[11px] text-gray-600 truncate">{tag.label}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <>
                <p className="text-[10px] text-gray-400 uppercase font-bold mb-2">Drag fields onto document</p>
                <div className="space-y-1">
                  {FIELD_TYPES.map(ft => (
                    <div key={ft.type} draggable onDragStart={() => handleDragStart(ft.type)}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg cursor-grab active:cursor-grabbing bg-white border border-gray-200 hover:border-gray-400 hover:shadow-sm transition-all text-sm"
                      data-testid={`field-type-${ft.type}`}
                    >
                      <ft.icon className="w-4 h-4 flex-shrink-0" style={{ color: ft.color }} />
                      <span className="text-gray-700 text-xs font-medium">{ft.label}</span>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Tips */}
            <div className="mt-4 pt-3 border-t border-gray-200">
              <p className="text-[10px] text-gray-400 uppercase font-bold mb-2">Tips</p>
              <p className="text-[10px] text-gray-500">Drag tags/fields onto the PDF</p>
              <p className="text-[10px] text-gray-500">Click + drag to reposition</p>
              <p className="text-[10px] text-gray-500">Drag corners/edges to resize</p>
              <p className="text-[10px] text-gray-500">Double-click form fields to edit label</p>
            </div>

            {/* Selected Field Properties */}
            {selectedFieldData && (
              <div className="mt-4 pt-3 border-t border-gray-200">
                <p className="text-[10px] text-gray-400 uppercase font-bold mb-2">Field Properties</p>
                <div className="space-y-2">
                  {selectedFieldData.type === 'data_tag' ? (
                    <>
                      <div>
                        <label className="text-[10px] text-gray-500">Data Tag</label>
                        <div className="text-xs font-mono bg-gray-100 px-2 py-1 rounded text-gray-700 mt-0.5">{selectedFieldData.tag_key}</div>
                      </div>
                      <div>
                        <label className="text-[10px] text-gray-500">Font Size</label>
                        <Input type="number" value={selectedFieldData.font_size || 9} onChange={e => updateField(selectedFieldData.id, 'font_size', parseInt(e.target.value) || 9)} className="h-7 text-xs bg-white" min={5} max={24} data-testid="field-font-size" />
                      </div>
                    </>
                  ) : (
                    <div>
                      <label className="text-[10px] text-gray-500">Label</label>
                      <Input value={selectedFieldData.label} onChange={e => updateField(selectedFieldData.id, 'label', e.target.value)} className="h-7 text-xs bg-white" data-testid="field-label-input" />
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Switch checked={selectedFieldData.required} onCheckedChange={v => updateField(selectedFieldData.id, 'required', v)} className="scale-75" />
                    <span className="text-[10px] text-gray-500">Required</span>
                  </div>
                  {selectedFieldData.type === 'dropdown' && (
                    <div>
                      <label className="text-[10px] text-gray-500">Options (one per line)</label>
                      <textarea value={(selectedFieldData.options || []).join('\n')} onChange={e => updateField(selectedFieldData.id, 'options', e.target.value.split('\n').filter(Boolean))}
                        className="w-full text-xs border border-gray-200 rounded p-1.5 mt-0.5 h-16 bg-white" />
                    </div>
                  )}
                  <Button variant="outline" size="sm" className="w-full text-xs text-red-500 border-red-200 hover:bg-red-50 h-7" onClick={() => removeField(selectedFieldData.id)}>
                    <Trash2 className="w-3 h-3 mr-1" /> Delete Field
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Center - Document Preview */}
        <div className="flex-1 overflow-y-auto bg-gray-200 p-6" onClick={() => { setSelectedField(null); if (editingField) saveInlineEdit(); }}>
          {pages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <FileText className="w-16 h-16 text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-600 mb-2">Upload a PDF Document</h3>
              <p className="text-sm text-gray-400 mb-4 max-w-sm">Upload any PDF to start adding data tags and form fields</p>
              <Button variant="outline" onClick={() => document.getElementById('pdf-upload-input').click()} disabled={uploading}>
                <Upload className="w-4 h-4 mr-1" /> {uploading ? 'Uploading...' : 'Choose PDF'}
              </Button>
            </div>
          ) : (
            <div className="max-w-3xl mx-auto space-y-4">
              {pages.map(pg => (
                <div key={pg.page} ref={el => pageRefs.current[pg.page] = el}
                  className="relative bg-white shadow-lg rounded" style={{ aspectRatio: `${pg.width} / ${pg.height}` }}
                  onDragOver={e => e.preventDefault()} onDrop={e => handlePageDrop(e, pg.page)} data-testid={`page-${pg.page}`}
                >
                  <img src={`${API_URL}${pg.url}`} alt={`Page ${pg.page}`} className="w-full h-full object-contain rounded" draggable={false} />
                  {/* Placed Fields */}
                  {(template.fields || []).filter(f => f.page === pg.page).map(field => {
                    const rect = pageRefs.current[pg.page]?.getBoundingClientRect();
                    if (!rect) return null;
                    const scaleX = rect.width / pg.width;
                    const scaleY = rect.height / pg.height;
                    const isDataTag = field.type === 'data_tag';
                    const fieldDef = !isDataTag ? FIELD_TYPES.find(ft => ft.type === field.type) : null;
                    const isSelected = selectedField === field.id;
                    const isEditing = editingField === field.id;
                    const color = isDataTag ? (field.color || CATEGORY_COLORS[field.category] || '#059669') : (fieldDef?.color || '#FBBF24');

                    return (
                      <div key={field.id}
                        className={`absolute flex items-center text-[10px] font-medium select-none ${isEditing ? '' : 'cursor-move'} ${isSelected ? 'ring-2 ring-blue-500 z-10' : 'hover:ring-1 hover:ring-blue-300'}`}
                        style={{
                          left: field.x * scaleX, top: field.y * scaleY,
                          width: field.width * scaleX, height: field.height * scaleY,
                          backgroundColor: `${color}22`, borderColor: color,
                          borderWidth: isDataTag ? 2 : 1.5, borderStyle: isDataTag ? 'dashed' : 'solid', borderRadius: 3,
                        }}
                        onMouseDown={e => !isEditing && handleFieldMouseDown(e, field)}
                        onClick={e => { e.stopPropagation(); setSelectedField(field.id); }}
                        onDoubleClick={e => handleFieldDoubleClick(e, field)}
                        data-testid={`placed-field-${field.id}`}
                      >
                        {isEditing ? (
                          <input ref={labelInputRef} type="text" value={editingLabel} onChange={e => setEditingLabel(e.target.value)}
                            onKeyDown={handleEditKeyDown} onBlur={saveInlineEdit} onClick={e => e.stopPropagation()}
                            className="w-full h-full bg-white/95 border-0 text-center text-[10px] font-medium outline-none px-1" style={{ color }}
                            data-testid="inline-label-input" />
                        ) : (
                          <span style={{ color }} className="truncate px-1 flex items-center gap-1">
                            {isDataTag && <Tag className="w-2.5 h-2.5 flex-shrink-0" />}
                            {field.label}
                          </span>
                        )}
                        {isSelected && !isEditing && <ResizeHandles field={field} color={color} />}
                        {isSelected && !isEditing && (
                          <button className="absolute -top-2 -right-2 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center text-[8px] hover:bg-red-600 z-30"
                            onClick={e => { e.stopPropagation(); removeField(field.id); }} data-testid="delete-field-btn">x</button>
                        )}
                      </div>
                    );
                  })}
                  <div className="absolute bottom-2 right-2 bg-black/50 text-white text-[10px] px-2 py-0.5 rounded">
                    Page {pg.page} of {pages.length}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
